import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { type PromotionPlacement } from '@onr/schema'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/atoms/Table/Table'
import { PromotionStatusIcon } from '@/components/molecules/PromotionStatusIcon'
import { cn } from '@/lib/utils'

import { type Promotion } from '../../model/Promotion'
import { type PromotionStatusIconProps } from '../../molecules/PromotionStatusIcon/PromotionStatusIcon'

export interface PromotionTableProps {
  promotions?: Promotion[]
  enabledPlacements?: string[]
  title?: string
  className?: string
  onRowClick?: (_id: string) => void
}

const StatusCell = ({ value, type }: PromotionStatusIconProps) => {
  if (!value) return <TableCell className="text-center" />

  return (
    <TableCell className="max-w-24 text-center">
      <div className="flex flex-col items-center gap-1">
        <PromotionStatusIcon value={value} type={type} />
      </div>
    </TableCell>
  )
}

const getPlacementLabel = (placement: PromotionPlacement, intl: ReturnType<typeof useIntl>): string => {
  switch (placement) {
    case 'email':
      return intl.formatMessage({ id: 'promotions.table.pilotEmails' })
    case 'milestone':
      return intl.formatMessage({ id: 'promotions.table.volunteerMilestones' })
    case 'hero':
      return intl.formatMessage({ id: 'promotions.table.mixedShowcases' })
    case 'volunteerProfile':
      return intl.formatMessage({ id: 'promotions.table.volunteerProfile' })
    case 'volunteerInfo':
      return intl.formatMessage({ id: 'promotions.table.volunteerInfo' })
    case 'clientInfo':
      return intl.formatMessage({ id: 'promotions.table.clientInfo' })
    default:
      return ''
  }
}

const getPlacementIconType = (placement: PromotionPlacement): PromotionStatusIconProps['type'] => {
  switch (placement) {
    case 'email':
      return 'email'
    case 'milestone':
      return 'star'
    default:
      return 'carrousel'
  }
}

export const PromotionTable = ({
  promotions = [],
  enabledPlacements: enabledPlacementsProp,
  title,
  className,
  onRowClick,
}: PromotionTableProps) => {
  const intl = useIntl()

  const visiblePlacements = useMemo(() => {
    const allPlacements = [
      'email',
      'milestone',
      'hero',
      'volunteerProfile',
      'volunteerInfo',
      'clientInfo',
    ] as PromotionPlacement[]

    if (!enabledPlacementsProp) {
      return allPlacements
    }

    return allPlacements.filter((placement) => enabledPlacementsProp.includes(placement))
  }, [enabledPlacementsProp])

  return (
    <div className={cn('w-full', className)}>
      <Table className="border border-gray-200">
        <TableHeader>
          <TableRow>
            <TableHead className="max-w-24 text-base font-bold">
              {title ?? intl.formatMessage({ id: 'promotions.table.partnerName' })}
            </TableHead>
            {visiblePlacements.map((placement) => (
              <TableHead key={placement} className="max-w-24 text-center text-base font-bold">
                {getPlacementLabel(placement, intl)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {promotions.map((promotion) => (
            <TableRow
              key={promotion._id}
              onClick={() => onRowClick?.(promotion._id)}
              className="cursor-pointer hover:bg-gray-50">
              <TableCell>
                <div className="min-h-8">
                  <p className="text-base font-medium text-black">{promotion.partnerName}</p>
                  <p className="mt-0.5 text-sm text-gray-500">{promotion.title}</p>
                </div>
              </TableCell>
              {visiblePlacements.map((placement) => (
                <StatusCell
                  key={placement}
                  value={promotion.placements?.includes(placement) ? (promotion.active ? 'enabled' : 'disabled') : null}
                  type={getPlacementIconType(placement)}
                />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
